import { z } from 'zod';
import { BaseResponseSchema, uuidSchema, } from './primitives.js';
// ── Location Integration (Cloud-API Site Registration) ──────────────────────
export const LocationIntegrationSiteParamsSchema = z.object({
    siteId: z.string().min(1).max(128),
});
export const DeploymentSiteScopeQuerySchema = z.object({
    tenantId: uuidSchema,
});
/** Deployment-scoped anchor registry row sourced from non-IoT inventory. */
export const DeploymentAnchorConfigRecordSchema = z.object({
    serialNumber: z.string().min(1),
    deviceModel: z.string().nullable().optional(),
    vendorSerialNumber: z.string().nullable().optional(),
    vendorDeviceId: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
});
export const DeploymentAnchorConfigListResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        anchors: z.array(DeploymentAnchorConfigRecordSchema),
        count: z.number().int().nonnegative(),
    }),
});
export const LocationIntegrationVendorTypeSchema = z.enum(['kontakt', 'pozyx', 'indooratlas']);
export const LocationIntegrationStatusSchema = z.enum([
    'not_configured',
    'configured_inactive',
    'configured_no_recent_data',
    'no_floor_plan',
    'no_registered_tags',
    'active',
]);
export const LocationIntegrationRequestSchema = z.object({
    tenantId: uuidSchema,
    vendorType: LocationIntegrationVendorTypeSchema,
    apiBaseUrl: z.string().url(),
    orgId: z.string().max(256).optional(),
    pollIntervalMs: z.number().int().min(60000).max(1800000).default(900000),
    enabled: z.boolean().default(true),
    floorPlanMap: z.record(z.string()).default({}),
    /** Vendor room/zone id → internal FloorZone zoneId (UUID), populated by import wizard */
    roomMap: z.record(z.string()).default({}),
    credentials: z.record(z.string()),
    /** Site master switch for cloud-computed zone enter/exit alarms. */
    cloudZoneAlarmsEnabled: z.boolean().optional(),
});
export const LocationIntegrationUpdateRequestSchema = LocationIntegrationRequestSchema.partial().extend({
    tenantId: uuidSchema,
});
export const LocationIntegrationConfigSchema = z.object({
    siteId: z.string(),
    tenantId: z.string(),
    vendorType: z.string(),
    orgId: z.string().optional(),
    apiBaseUrl: z.string(),
    pollIntervalMs: z.number(),
    enabled: z.boolean(),
    floorPlanMap: z.record(z.string()),
    roomMap: z.record(z.string()).optional().default({}),
    lastRunAt: z.string().nullable(),
    /** ISO timestamp of last Kontakt/vendor topology sync (manual or scheduled) */
    lastTopologySyncAt: z.string().nullable().optional(),
    /** Site master switch for cloud-computed zone enter/exit alarms. */
    cloudZoneAlarmsEnabled: z.boolean().default(false),
    createdAt: z.string(),
    updatedAt: z.string().nullable(),
});
export const SiteTelemetrySettingsDataSchema = z.object({
    siteId: z.string(),
    cloudZoneAlarmsEnabled: z.boolean(),
    zoneAlarmsEnabled: z.boolean(),
});
export const SiteTelemetrySettingsPatchBodySchema = z.object({
    cloudZoneAlarmsEnabled: z.boolean(),
});
export const SiteTelemetrySettingsResponseSchema = BaseResponseSchema.extend({
    data: SiteTelemetrySettingsDataSchema,
});
/** Provider site telemetry — includes dev/testing all-tags override (not exposed to tenants). */
export const ProviderSiteTelemetrySettingsDataSchema = SiteTelemetrySettingsDataSchema.extend({
    cloudZoneAlarmsAllTagsEnabled: z.boolean(),
});
export const ProviderSiteTelemetrySettingsPatchBodySchema = z.object({
    cloudZoneAlarmsEnabled: z.boolean().optional(),
    cloudZoneAlarmsAllTagsEnabled: z.boolean().optional(),
});
export const ProviderSiteTelemetrySettingsResponseSchema = BaseResponseSchema.extend({
    data: ProviderSiteTelemetrySettingsDataSchema,
});
/** GET may return `data: null` when no integration exists (HTTP 200 — avoids noisy browser console on “not configured”). */
export const LocationIntegrationResponseSchema = BaseResponseSchema.extend({
    data: z.object({ integration: LocationIntegrationConfigSchema }).nullable(),
});
export const LocationIntegrationDeleteResponseSchema = BaseResponseSchema.extend({
    data: z.object({ siteId: z.string(), tenantId: z.string(), deleted: z.boolean() }),
});
export const LocationIntegrationPollNowResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        tenantId: z.string(),
        siteId: z.string(),
        invokedAt: z.string(),
        ok: z.boolean(),
        message: z.string(),
    }),
});
export const LocationIntegrationTestRequestSchema = LocationIntegrationRequestSchema.pick({
    tenantId: true,
    vendorType: true,
    apiBaseUrl: true,
    orgId: true,
    credentials: true,
});
export const LocationIntegrationTestResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        tenantId: z.string(),
        siteId: z.string(),
        vendorType: LocationIntegrationVendorTypeSchema,
        ok: z.boolean(),
        httpStatus: z.number().int().nullable(),
        floorsDetected: z.number().int().nonnegative(),
        activeTagsVisible: z.number().int().nonnegative(),
        message: z.string(),
        checkedAt: z.string(),
    }),
});
export const LocationIntegrationPollAttemptSchema = z.object({
    startedAt: z.string(),
    completedAt: z.string().nullable(),
    durationMs: z.number().int().nonnegative().nullable(),
    status: z.enum(['success', 'error', 'skipped']),
    tagsPolled: z.number().int().nonnegative(),
    /** Distinct tag IDs seen in this poll (uppercase). Used for 24h unique-node metrics. */
    uniqueTagIds: z.array(z.string()).optional(),
    historyEventsWritten: z.number().int().nonnegative(),
    errorMessage: z.string().nullable(),
});
export const LocationIntegrationHealthResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        tenantId: z.string(),
        siteId: z.string(),
        status: z.enum(['healthy', 'degraded', 'error', 'not_configured']),
        description: z.string(),
        lastPollAt: z.string().nullable(),
        metrics: z.object({
            vendorFloorsObserved: z.number().int().nonnegative(),
            tagsSeenLast24h: z.number().int().nonnegative(),
            historyEventsWrittenLast24h: z.number().int().nonnegative(),
        }),
        recentPolls: z.array(LocationIntegrationPollAttemptSchema),
        onboardingChecklist: z.array(z.object({
            key: z.string(),
            label: z.string(),
            complete: z.boolean(),
        })),
    }),
});
/** Preview topology before committing import (provider wizard step 1) */
export const LocationIntegrationWizardPreviewResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        tenantId: z.string(),
        siteId: z.string(),
        vendorType: z.string(),
        floors: z.array(z.object({
            vendorFloorId: z.string(),
            name: z.string(),
            level: z.number().optional(),
            widthMeters: z.number().optional(),
            heightMeters: z.number().optional(),
        })),
    }),
});
/** Full vendor import / wizard run result */
export const LocationIntegrationWizardRunResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        siteId: z.string(),
        tenantId: z.string(),
        vendorType: z.string(),
        floorsCreated: z.number().int().nonnegative(),
        zonesCreated: z.number().int().nonnegative(),
        tagsRegistered: z.number().int().nonnegative(),
        floorPlanMap: z.record(z.string()),
        roomMap: z.record(z.string()),
        messages: z.array(z.string()).optional(),
    }),
});
export const LocationIntegrationWizardSyncResponseSchema = LocationIntegrationWizardRunResponseSchema;
export const TenantIntegrationStatusResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        siteId: z.string(),
        status: LocationIntegrationStatusSchema,
        configured: z.boolean(),
        enabled: z.boolean(),
        hasRecentData: z.boolean(),
        hasFloorPlan: z.boolean(),
        registeredTagCount: z.number().int().nonnegative(),
        lastRunAt: z.string().nullable(),
        message: z.string(),
        /** Set when a cloud vendor integration is configured for this site. */
        vendorType: LocationIntegrationVendorTypeSchema.nullable().optional(),
        /** Read-only alias of site cloudZoneAlarmsEnabled for tenant UI gating. */
        zoneAlarmsEnabled: z.boolean().optional(),
    }),
});
// ── Site Access Grants (Deployment Engineer Access) ──────────────────────────
export const SiteAccessGrantSchema = z.object({
    grantId: uuidSchema,
    engineerUserId: uuidSchema,
    tenantId: uuidSchema,
    siteId: z.string().min(1).max(128),
    /** Display name at grant time (optional; older grants may omit). */
    tenantName: z.string().max(128).nullable().optional(),
    /** Display label at grant time (optional; older grants may omit). */
    siteName: z.string().max(128).nullable().optional(),
    grantedBy: uuidSchema,
    grantedAt: z.string().datetime(),
    expiresAt: z.string().datetime().nullable(),
    revokedAt: z.string().datetime().nullable(),
    reason: z.string().max(512).nullable(),
});
export const SiteAccessGrantCreateBodySchema = z.object({
    engineerUserId: uuidSchema,
    tenantId: uuidSchema,
    siteId: z.string().min(1).max(128),
    expiresAt: z.string().datetime().nullable().optional(),
    reason: z.string().max(512).optional(),
    tenantName: z.string().max(128).optional(),
    siteName: z.string().max(128).optional(),
});
export const SiteAccessGrantUpdateBodySchema = z.object({
    expiresAt: z.string().datetime().nullable().optional(),
    revokedAt: z.string().datetime().nullable().optional(),
    reason: z.string().max(512).optional(),
});
export const GrantIdParamsSchema = z.object({
    grantId: uuidSchema,
});
export const SiteAccessGrantListResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        grants: z.array(SiteAccessGrantSchema),
        total: z.number().int(),
    }),
});
export const SiteAccessGrantResponseSchema = BaseResponseSchema.extend({
    data: z.object({ grant: SiteAccessGrantSchema }),
});
// ── Deployment Engineer Accessible Sites ──────────────────────────────────────
export const DeploymentEngineerSiteSchema = z.object({
    siteId: z.string().min(1).max(128),
    tenantId: uuidSchema,
    tenantName: z.string().nullable(),
    siteName: z.string().nullable(),
    grantId: uuidSchema.nullable(),
    expiresAt: z.string().datetime().nullable(),
});
export const DeploymentEngineerSitesResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        sites: z.array(DeploymentEngineerSiteSchema),
        total: z.number().int(),
    }),
});
// ── Provider Operations Dashboard ────────────────────────────────────────────
export const IntegrationHealthStatusSchema = z.enum(['healthy', 'degraded', 'error', 'not_configured']);
export const IntegrationHealthCardSchema = z.object({
    tenantId: uuidSchema,
    tenantName: z.string().nullable(),
    siteId: z.string().min(1).max(128),
    siteName: z.string().nullable(),
    vendorType: z.string().nullable(),
    status: IntegrationHealthStatusSchema,
    lastPollAt: z.string().nullable(),
    tagsSeenLastHour: z.number().int().nonnegative(),
    floorMappingCompleteness: z.number().min(0).max(1),
    mappedFloorCount: z.number().int().nonnegative(),
    importedFloorCount: z.number().int().nonnegative(),
});
export const ProviderOperationsDashboardResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        totalActive: z.number().int(),
        totalError: z.number().int(),
        totalIncompleteMapping: z.number().int(),
        totalNoRecentPoll: z.number().int(),
        integrations: z.array(IntegrationHealthCardSchema),
    }),
});
// ── UWB Deployment Status Dashboard ──────────────────────────────────────────
export const UwbSiteDeploymentCardSchema = z.object({
    tenantId: uuidSchema,
    tenantName: z.string().nullable(),
    siteId: z.string().min(1).max(128),
    siteName: z.string().nullable(),
    floorPlanCount: z.number().int().nonnegative(),
    totalAnchorPoints: z.number().int().nonnegative(),
    assignedAnchorCount: z.number().int().nonnegative(),
    lastPushAt: z.string().nullable(),
    lastPushVersion: z.string().nullable(),
    hasFloorPlan: z.boolean(),
    anchorAssignmentComplete: z.boolean(),
    /** Assigned anchor points reporting online in location current state. */
    anchorsOnlineCount: z.number().int().nonnegative().default(0),
    /** Assigned anchor points offline or stale in location current state. */
    anchorsOfflineCount: z.number().int().nonnegative().default(0),
});
export const UwbDeploymentStatusResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        totalSites: z.number().int(),
        sitesWithFloorPlan: z.number().int(),
        sitesFullyAssigned: z.number().int(),
        sitesPushed: z.number().int(),
        sites: z.array(UwbSiteDeploymentCardSchema),
    }),
});
// ── LS-1000 Config Push History ──────────────────────────────────────────────
export const FloorPlanSiteParamsSchema = z.object({
    siteId: z.string().min(1).max(128),
});
export const FloorPlanFloorParamsSchema = z.object({
    siteId: z.string().min(1).max(128),
    floorId: z.string().uuid(),
});
export const AnchorPositionSchema = z.object({
    serialNumber: z.string(),
    pixelX: z.number().nullable(),
    pixelY: z.number().nullable(),
    uwbX: z.number().nullable(),
    uwbY: z.number().nullable(),
    isManualUWB: z.boolean().optional().default(false),
});
export const AnchorPointTdoaRoleSchema = z.enum(['none', 'master', 'branch', 'leaf']);
export const GlobalPointSchema = z.object({
    xM: z.number(),
    yM: z.number(),
    zM: z.number().nullable().optional(),
});
export const GlobalBoundarySchema = z.object({
    shape: z.enum(['rectangle', 'polygon']),
    points: z.array(GlobalPointSchema).min(2),
});
export const FloorPlanControlPointSchema = z.object({
    id: z.string().min(1).max(64),
    label: z.string().min(1).max(128),
    role: z.enum(['origin', 'reference']).default('reference'),
    pixelX: z.number(),
    pixelY: z.number(),
    layerXm: z.number(),
    layerYm: z.number(),
    layerZm: z.number().nullable().optional(),
    globalXm: z.number().nullable().optional(),
    globalYm: z.number().nullable().optional(),
    globalZm: z.number().nullable().optional(),
});
export const AnchorPointSchema = z.object({
    id: z.string().min(1).max(64),
    label: z.string().min(1).max(64),
    layerId: z.number().int().min(0).nullable().optional(),
    layerXm: z.number().nullable().optional(),
    layerYm: z.number().nullable().optional(),
    layerZm: z.number().nullable().optional(),
    globalXm: z.number().nullable().optional(),
    globalYm: z.number().nullable().optional(),
    globalZm: z.number().nullable().optional(),
    pixelX: z.number().nullable(),
    pixelY: z.number().nullable(),
    pixelZ: z.number().nullable().optional(),
    tdoaRole: AnchorPointTdoaRoleSchema.default('none'),
    tdoaSyncFromId: z.string().nullable().optional(),
    neighborIds: z.array(z.string()).default([]),
    zoneIds: z.array(z.string().uuid()).max(4).default([]),
    correctionCm: z.number().default(0),
    isMbs: z.boolean().default(false),
    assignedAnchorSerialNumber: z.string().nullable().optional(),
    updatedAt: z.string().nullable().optional(),
    updatedBy: z.string().nullable().optional(),
});
export const LocationGatewayAlgorithmParamsXmlSchema = z.string().trim().min(1).superRefine((value, context) => {
    if (value.includes('<group')) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Algorithm XML must contain only child parameters, not a <group> wrapper',
        });
    }
    if (!value.includes('<')) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Algorithm XML must contain at least one XML element',
        });
    }
});
/** Default TOF algorithm XML for Woxu LS-1000 engine project.xml */
export const DEFAULT_WOXU_LS1000_TOF_ALGORITHM_PARAMS_XML = [
    '<locatorSettings class="1" flat-layout="1" known-height="1" outside-check="1" relax-mode="0" speed-limit="1" />',
].join('\n');
/** Default TDOA algorithm XML for Woxu LS-1000 engine project.xml */
export const DEFAULT_WOXU_LS1000_TOA_ALGORITHM_PARAMS_XML = [
    '<locatorSettings class="3" cluster-check="0" known-height="1" speed-limit="1" use-weight="0" />',
    '<outFilterSettings class="5" filter-count="3" />',
].join('\n');
export const LocationGatewayTagBlinkFrequencyHzSchema = z.enum(['0.1', '0.2', '0.5', '1', '2', '4', '5', '10', '20']);
export const LocationGatewayForwardingTargetSchema = z.object({
    host: z.string().trim().min(1),
    port: z.number().int().min(1).max(65535),
});
const DEFAULT_LOCATION_GATEWAY_FORWARDING_TARGET = {
    host: '127.0.0.1',
    port: 5000,
};
export const LocationGatewayDiscoverySettingsSchema = z.object({
    enabled: z.boolean().default(true),
    periodMs: z.number().int().min(1).max(600000).default(3000),
    pollingIps: z.string().trim().min(1).default('192.168.0.200:192.168.0.203'),
});
export const LocationGatewayProjectXmlSettingsSchema = z.object({
    engineHost: z.string().trim().min(1).default('192.168.0.135'),
    samplingTransferPort: z.number().int().min(1).max(65535).default(44333),
    controlPort: z.number().int().min(1).max(65535).default(44332),
    discovery: LocationGatewayDiscoverySettingsSchema.default({}),
    locationGatewayForwardingTargets: z.array(LocationGatewayForwardingTargetSchema).min(1).optional(),
    udpForwardHost: z.string().trim().min(1).optional(),
    udpForwardPort: z.number().int().min(1).max(65535).optional(),
    tagBlinkFrequencyHz: LocationGatewayTagBlinkFrequencyHzSchema.default('0.2'),
    positioningMode: z.enum(['tof', 'tdoa']).default('tdoa'),
    tofAlgorithmParamsXml: LocationGatewayAlgorithmParamsXmlSchema.default(DEFAULT_WOXU_LS1000_TOF_ALGORITHM_PARAMS_XML),
    toaAlgorithmParamsXml: LocationGatewayAlgorithmParamsXmlSchema.default(DEFAULT_WOXU_LS1000_TOA_ALGORITHM_PARAMS_XML),
}).transform((settings) => {
    const locationGatewayForwardingTargets = settings.locationGatewayForwardingTargets
        ?? (settings.udpForwardHost && settings.udpForwardPort
            ? [{ host: settings.udpForwardHost, port: settings.udpForwardPort }]
            : [{ ...DEFAULT_LOCATION_GATEWAY_FORWARDING_TARGET }]);
    return {
        engineHost: settings.engineHost,
        samplingTransferPort: settings.samplingTransferPort,
        controlPort: settings.controlPort,
        discovery: settings.discovery,
        locationGatewayForwardingTargets,
        tagBlinkFrequencyHz: settings.tagBlinkFrequencyHz,
        positioningMode: settings.positioningMode,
        tofAlgorithmParamsXml: settings.tofAlgorithmParamsXml,
        toaAlgorithmParamsXml: settings.toaAlgorithmParamsXml,
    };
});
// A 2-D pixel coordinate point used for zone/exclusion boundaries
export const PixelPointSchema = z.object({
    pixelX: z.number(),
    pixelY: z.number(),
});
export const ZoneBoundarySchema = z.object({
    shape: z.enum(['rectangle', 'polygon']),
    points: z.array(PixelPointSchema).min(2),
});
// A zone is a named area on a floor that reflects real-world business semantics —
// rooms, bays, corridors, clean areas. Defined and named by the tenant.
// Zones do NOT contain anchor assignments, algorithm overrides, or RF parameters
// (those belong to UwbGroup).
export const FloorZoneSchema = z.object({
    zoneId: z.string().uuid(),
    zoneName: z.string().min(1).max(64),
    boundary: ZoneBoundarySchema.nullable().optional(),
    globalBoundary: GlobalBoundarySchema.nullable().optional(),
    /** When true, cloud zone enter/exit alarms may fire for this polygon (requires site + tag gates). */
    zoneAlarmsEnabled: z.boolean().optional(),
});
// A UWB group is a positioning cluster passed to the site location engine (Woxu LS-1000 today).
// It defines which anchors participate in ranging for a given area, and the
// algorithm settings for that cluster. UWB groups are a deployment-engineer concern
// and are invisible to tenants.
export const UwbGroupSchema = z.object({
    groupId: z.number().int().min(0).max(255),
    groupName: z.string().min(1).max(64),
    /** Pixel-space boundary for UWB anchor grouping in engine project.xml. Distinct from tenant floor zones. */
    boundary: ZoneBoundarySchema.nullable().optional(),
    anchorPointIds: z.array(z.string()).default([]),
    algorithmOverride: z.enum(['inherit', 'tof', 'tdoa']).default('inherit'),
    algorithmParamsXmlOverride: LocationGatewayAlgorithmParamsXmlSchema.optional(),
    /** Legacy link to a tenant zone; optional when groups are defined only by boundary. */
    zoneRef: z.string().uuid().optional(),
});
// ExclusionZone and LayerSettings have been removed.
// Algorithm parameters (positioningMode, kalmanCount, constraintArea, exclusionZones)
// are now per-UwbGroup via algorithmParamsXmlOverride.
// Floor-level Z is encoded in the reference point's Z coordinate (controlPoints).
// globalOriginOffset fields are replaced by the calibration reference point system.
export const FloorPlanUploadBodySchema = z.object({
    floorId: z.string().uuid().optional(),
    displayName: z.string().min(1).max(128),
    coordinateScale: z.coerce.number().positive().default(1.0),
    originOffsetX: z.coerce.number().default(0),
    originOffsetY: z.coerce.number().default(0),
    isDefault: z.coerce.boolean().default(false),
    layerId: z.coerce.number().int().min(0).optional(),
});
function isBrowserFile(value) {
    const FileCtor = globalThis.File;
    return typeof FileCtor === 'function' && value instanceof FileCtor;
}
/** FormData body for POST /location/sites/:siteId/floor-plans. */
export const FloorPlanUploadFormSchema = FloorPlanUploadBodySchema.extend({
    image: z.custom(isBrowserFile),
});
export const FloorPlanUpdateBodySchema = z.object({
    displayName: z.string().min(1).max(128).optional(),
    coordinateScale: z.number().positive().optional(),
    originOffsetX: z.number().optional(),
    originOffsetY: z.number().optional(),
    isDefault: z.boolean().optional(),
    layerId: z.number().int().min(0).optional(),
    geoOriginLat: z.number().min(-90).max(90).optional(),
    geoOriginLng: z.number().min(-180).max(180).optional(),
    geoNorthBearing: z.number().min(0).max(360).optional(),
    floorElevationM: z.number().optional(),
    anchorPositions: z.array(AnchorPositionSchema).optional(),
    anchorPoints: z.array(AnchorPointSchema).optional(),
    controlPoints: z.array(FloorPlanControlPointSchema).optional(),
    // Geo-reference pin coordinates (pixel space + map lat/lng)
    georefPin1X: z.number().optional(),
    georefPin1Y: z.number().optional(),
    georefPin2X: z.number().optional(),
    georefPin2Y: z.number().optional(),
    georefMapPin1Lat: z.number().min(-90).max(90).optional(),
    georefMapPin1Lng: z.number().min(-180).max(180).optional(),
    georefMapPin2Lat: z.number().min(-90).max(90).optional(),
    georefMapPin2Lng: z.number().min(-180).max(180).optional(),
    // Local calibration reference point (pixel + known local coords)
    calibRefPx: z.number().optional(),
    calibRefPy: z.number().optional(),
    calibRefUwbX: z.number().optional(),
    calibRefUwbY: z.number().optional(),
    // Zone and UWB group settings
    zones: z.array(FloorZoneSchema).optional(),
    uwbGroups: z.array(UwbGroupSchema).optional(),
    // Free-text tags for search and filtering
    tags: z.array(z.string()).optional(),
});
export const FloorPlanSourceTypeSchema = z.enum(['uwb', 'vendor_import']);
export const FloorPlanConfigSchema = z.object({
    floorId: z.string().uuid(),
    siteId: z.string(),
    tenantId: z.string(),
    displayName: z.string(),
    coordinateScale: z.number(),
    originOffsetX: z.number(),
    originOffsetY: z.number(),
    isDefault: z.boolean(),
    layerId: z.number().int().nullable().optional(),
    /** uwb = Cnapse-managed floor; vendor_import = cloud vendor (Kontakt, etc.). */
    sourceType: FloorPlanSourceTypeSchema.optional().default('uwb'),
    /** Vendor floor identifier when sourceType is vendor_import. */
    vendorFloorId: z.string().optional(),
    imageObjectKey: z.string().nullable(),
    /** Canonical SVG pixel width of the floor plan image (from upload or vendor import). */
    imageWidth: z.number().int().positive().nullable().optional(),
    /** Canonical SVG pixel height of the floor plan image (from upload or vendor import). */
    imageHeight: z.number().int().positive().nullable().optional(),
    createdAt: z.string(),
    updatedAt: z.string().nullable(),
    geoOriginLat: z.number().nullable().optional(),
    geoOriginLng: z.number().nullable().optional(),
    geoNorthBearing: z.number().nullable().optional(),
    floorElevationM: z.number().nullable().optional(),
    anchorPositions: z.array(AnchorPositionSchema).optional().default([]),
    anchorPoints: z.array(AnchorPointSchema).optional().default([]),
    controlPoints: z.array(FloorPlanControlPointSchema).optional().default([]),
    // Geo-reference pin coordinates
    georefPin1X: z.number().nullable().optional(),
    georefPin1Y: z.number().nullable().optional(),
    georefPin2X: z.number().nullable().optional(),
    georefPin2Y: z.number().nullable().optional(),
    georefMapPin1Lat: z.number().nullable().optional(),
    georefMapPin1Lng: z.number().nullable().optional(),
    georefMapPin2Lat: z.number().nullable().optional(),
    georefMapPin2Lng: z.number().nullable().optional(),
    // Local calibration reference point (pixel + known local coords)
    calibRefPx: z.number().nullable().optional(),
    calibRefPy: z.number().nullable().optional(),
    calibRefUwbX: z.number().nullable().optional(),
    calibRefUwbY: z.number().nullable().optional(),
    // Zone and UWB group settings
    zones: z.array(FloorZoneSchema).optional().default([]),
    uwbGroups: z.array(UwbGroupSchema).optional().default([]),
    // Free-text tags for search and filtering in the UI (e.g. "office", "parking", "building-a")
    tags: z.array(z.string()).optional().default([]),
});
export const FloorPlanConfigWithUrlSchema = FloorPlanConfigSchema.extend({
    imageUrl: z.string().nullable(),
});
export const FloorPlanListResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        siteId: z.string(),
        floorPlans: z.array(FloorPlanConfigWithUrlSchema),
    }),
});
export const FloorPlanResponseSchema = BaseResponseSchema.extend({
    data: z.object({ floorPlan: FloorPlanConfigWithUrlSchema }),
});
export const FloorPlanDeleteResponseSchema = BaseResponseSchema.extend({
    data: z.object({ floorId: z.string(), deleted: z.boolean() }),
});
export const FloorPlanUpdateResponseSchema = BaseResponseSchema.extend({
    data: z.object({ siteId: z.string(), floorId: z.string(), updated: z.boolean() }),
});
export const FloorPlanImageUrlResponseSchema = BaseResponseSchema.extend({
    data: z.object({ floorId: z.string(), imageUrl: z.string() }),
});
export const SiteDeleteResponseSchema = BaseResponseSchema.extend({
    data: z.object({ siteId: z.string(), deleted: z.boolean() }),
});
export const AnchorPointAssignmentBodySchema = z.object({
    assignedAnchorSerialNumber: z.string().nullable(),
});
export const FloorPlanAnchorPointParamsSchema = z.object({
    siteId: z.string().min(1).max(128),
    floorId: z.string().uuid(),
    anchorPointId: z.string().min(1).max(64),
});
export const AnchorPointResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        siteId: z.string(),
        floorId: z.string().uuid(),
        anchorPoint: AnchorPointSchema,
    }),
});
// ── Location gateway config push (site-scoped) ───────────────────────────────
export const LocationGatewayConfigPushBodySchema = z.object({
    /** Optional floor plan to generate config from; defaults to site's default floor */
    floorId: z.string().min(1).max(128).optional(),
    /** The specific Gateway Serial Number to notify via MQTT */
    serialNumber: z.string().min(1),
    /** Optional TCP forwarding host to embed in project.xml */
    forwardingHost: z.string().optional(),
    /** Optional TCP forwarding port to embed in project.xml */
    forwardingPort: z.number().int().min(1).max(65535).optional(),
    /** Push-time override layer (not persisted). Merged for this push only. */
    pushTimeOverrides: z.record(z.string(), z.unknown()).optional(),
});
export const LocationGatewayConfigPushParamsSchema = z.object({
    siteId: z.string().min(1).max(128),
});
export const LocationGatewayConfigPushResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        version: z.string(),
        siteId: z.string(),
        projectXmlKey: z.string(),
        adefIniKey: z.string(),
        manifestKey: z.string(),
        manifestUrl: z.string().url(),
        projectXmlUrl: z.string().url(),
        adefIniUrl: z.string().url(),
        warnings: z.array(z.string()).default([]),
        pushedAt: z.string().datetime(),
        status: z.enum([
            's3_uploaded',
            'mqtt_sent',
            's3_uploaded_mqtt_failed',
            'applied',
            'apply_failed',
            'apply_timeout',
            'superseded',
        ]).optional(),
        mqttRequestId: z.string().uuid().optional(),
    }),
});
// ── UWB Location Read / Command (§4.2, §4.3) ────────────────────────────────
export const UwbSiteParamsSchema = z.object({
    siteId: z.string().min(1).max(128),
});
export const UwbTagParamsSchema = z.object({
    siteId: z.string().min(1).max(128),
    serialNumber: z.string().min(1).max(64),
});
export const TagCurrentStateSchema = z.object({
    serialNumber: z.string(),
    siteId: z.string().optional(),
    x: z.number().optional(),
    y: z.number().optional(),
    z: z.number().optional(),
    layerId: z.number().optional(),
    floorId: z.string().optional(),
    zoneId: z.string().optional(),
    zoneName: z.string().optional(),
    floorLabel: z.string().optional(),
    positionKind: z.enum(['precise', 'zone-only']).optional(),
    online: z.boolean(),
    battery: z.number().nullable(),
    sosActive: z.boolean(),
    firmwareVersion: z.string().nullable(),
    lastSeenAt: z.string(),
    assetId: z.number().optional(),
    assetName: z.string().optional(),
});
export const TagCurrentStateListResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        siteId: z.string(),
        tags: z.array(TagCurrentStateSchema),
        queryTime: z.string(),
    }),
});
export const AnchorHardwareSchema = z.object({
    serialNumber: z.string(),
    x: z.number().optional(),
    y: z.number().optional(),
    z: z.number().optional(),
    layerId: z.number().optional(),
    online: z.boolean(),
    battery: z.number().nullable(),
    firmwareVersion: z.string().nullable(),
    lastSeenAt: z.string(),
    assignedFloorId: z.string().uuid().nullable().optional(),
    assignedFloorName: z.string().nullable().optional(),
    assignedAnchorPointId: z.string().nullable().optional(),
    assignedAnchorPointLabel: z.string().nullable().optional(),
    assignedAnchorPointRole: AnchorPointTdoaRoleSchema.nullable().optional(),
});
export const AnchorHardwareListResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        siteId: z.string(),
        anchors: z.array(AnchorHardwareSchema),
        queryTime: z.string(),
    }),
});
export const HistoryQuerySchema = z.object({
    from: z.string().datetime(),
    to: z.string().datetime().optional(),
    maxPoints: z.coerce.number().int().min(1).max(5000).default(500),
});
export const TagHistoryPointSchema = z.object({
    /** Epoch ms — same instant as `lastSeenAt` when the position was recorded. */
    timestamp: z.number(),
    x: z.number().optional(),
    y: z.number().optional(),
    z: z.number().optional(),
    layerId: z.number().optional(),
    floorId: z.string().optional(),
    zoneName: z.string().optional(),
    zoneId: z.string().optional(),
    floorLabel: z.string().optional(),
    positionKind: z.enum(['precise', 'zone-only']).optional(),
    battery: z.number().optional(),
    online: z.boolean().optional(),
    /** Cloud motion pipeline record kind (movement vs dwell interval). */
    recordType: z.enum(['movement', 'stationary']).optional(),
    /** For stationary rows expanded from intervalEnd — marks dwell close vs open. */
    stationaryPhase: z.enum(['start', 'end']).optional(),
});
export const TagHistoryResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        siteId: z.string(),
        serialNumber: z.string(),
        points: z.array(TagHistoryPointSchema),
        total: z.number().int(),
    }),
});
export const EventsQuerySchema = z.object({
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
    deviceId: z.string().optional(),
    eventType: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(500).default(100),
});
export const TagEventItemSchema = z.object({
    id: z.number(),
    siteId: z.string(),
    deviceType: z.string(),
    eventType: z.string(),
    alarmTypeRaw: z.string().nullable(),
    regionName: z.string().nullable(),
    positionX: z.number().nullable(),
    positionY: z.number().nullable(),
    layerId: z.number().nullable(),
    floorId: z.string().nullable().optional(),
    // Canonical Cnapse Serial Number (resolved/attested device identity)
    serialNumber: z.string().nullable(),
    occurredAt: z.string(),
    source: z.enum(['vendor', 'computed']).optional(),
    zoneId: z.string().uuid().nullable().optional(),
    zoneName: z.string().nullable().optional(),
});
export const EventsResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        siteId: z.string(),
        events: z.array(TagEventItemSchema),
        total: z.number().int(),
    }),
});
export const UwbCommandRequestSchema = z.object({
    requestId: z.string().uuid().optional(),
    type: z.enum(['WARN', 'EVACUATE', 'MESSAGE', 'SLEEP', 'WARN_ENHANCED']),
    tagIds: z.array(z.string()).min(1),
    message: z.string().max(32).optional(),
    motor: z.string().regex(/^[0-9a-fA-F]{2}$/).optional(),
    buzzer: z.string().regex(/^[0-9a-fA-F]{2}$/).optional(),
    led: z.string().regex(/^[0-9a-fA-F]{2}$/).optional(),
});
export const UwbCommandResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        requestId: z.string(),
        type: z.string(),
        siteId: z.string(),
        publishedAt: z.string(),
    }),
});
//# sourceMappingURL=location-integration.schemas.js.map